import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { FormGroup } from '@angular/forms';
import { TwRecintoViewModel } from '../recinto.types';
import { Tc } from 'src/app/components/core/_infrastructure/types/common';
import { EnumDescripcionTcTipoSolicitudCamSingular, EnumIdsTcTipoSolicitudCam } from '../../types/enum';

@Component({
    selector: 'app-recinto-edit',
    templateUrl: './recinto-edit.component.html',
})
export class RecintoEditComponent extends ComponentBase implements OnChanges, OnInit {
    @Input() editMode = true;
    @Input() dialogMode = false;
    @Input() recinto: TwRecintoViewModel;
    @Input() formGroup: FormGroup;
    @Input() provincias: Tc[];
    @Input() municipios: Tc[];
    @Input() tiposRecinto: Tc[];
    @Output() provinciaSelected = new EventEmitter<number>();
    @Output() showSigpac = new EventEmitter();

    isCollapsedCabecera = false;
    isCollapsedLocalizacion = false;
    superficieTotalHasChangedError = 'La Superficie Total ha cambiado. Por favor, revise y reestructure la Superficie Utilizada por sus cultivos.';

    constructor(
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.updateForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.recinto?.currentValue) {
            this.recinto = { ...changes.recinto.currentValue };
            this.updateForm();
        }
        if (changes?.municipios?.currentValue && this.recinto?.IdMunicipio) {
            this.setIdMunicipio(this.recinto?.IdMunicipio || null);
        }
    }

    /**
     * This functions sets the values in the recinto (@Input) into the form.
     */
    private updateForm(): void {
        this.formGroup.setValue({
            Id: this.recinto?.Id || null,
            Titular: this.recinto?.Titular || '',
            Nif: this.recinto?.Nif || '',
            IdTipoRecinto: this.recinto?.IdTipoRecinto || null,
            SuperficieTotal: this.recinto?.SuperficieTotal || 0.0,
            IdProvincia: this.recinto?.IdProvincia || null,
            IdMunicipio: this.recinto?.IdMunicipio || null,
            IdMunicipioSigPac: this.recinto?.IdMunicipioSigPac || null,
            Agregado: this.recinto?.Agregado || 0,
            Zona: this.recinto?.Zona || 0,
            Poligono: this.recinto?.Poligono || 0,
            Parcela: this.recinto?.Parcela || 0,
            Recinto: this.recinto?.Recinto || 0,
            UsoSigPac: this.recinto?.UsoSigPac || '', // hidden
            EsCompleta: this.recinto?.EsCompleta || false, // hidden
            FechaCreacion: this.recinto?.FechaCreacion || null, // hidden
        });

        if (this.recinto?.IdProvincia) {
            this.onSelectProvincia(null);
        }

        this.setDisableForInput();

        this.form = this.formGroup; // componentBase uses this.form
    }

    private setDisableForInput(): void {
        if (!this.recinto) {
            // On creation mode every field is enabled.
            // editMode should be set to false
            return;
        }

        this.formGroup.controls['IdTipoRecinto'].disable();
        this.formGroup.controls['IdProvincia'].disable();
        this.formGroup.controls['IdMunicipio'].disable();
        this.formGroup.controls['Agregado'].disable();
        this.formGroup.controls['Zona'].disable();
        this.formGroup.controls['Poligono'].disable();
        this.formGroup.controls['Parcela'].disable();
        this.formGroup.controls['Recinto'].disable();

        if (!this.isAdmin || !this.editMode) {
            this.formGroup.controls['Titular'].disable();
            this.formGroup.controls['Nif'].disable();
        }
    }

    /**
     * Manejador del evento de selección de una Provincia en el desplegable.
     * Ignoramos el valor que viene en el parámetro y lo tomamos del form.
     * @param selection Id y descripción de la provincia seleccionada
     */
    onSelectProvincia(selection: any): void {
        const idProvincia = this.formGroup.controls.IdProvincia.value;
        this.provinciaSelected.emit(idProvincia);
        this.setIdMunicipio(null);
    }

    /**
     *
     * @param selection Municipio seleccionado
     */
    onSelectMunicipio(selection: any): void {
        this.formGroup.patchValue({
            IdMunicipioSigPac: selection?.MunicipioSigpac,
        });
    }

    private setIdMunicipio(idMunicipio: number | null): void {
        this.formGroup.patchValue({
            IdMunicipio: idMunicipio,
        });
    }

    onShowSigpac(): void {
        this.showSigpac.emit();
    }

    get title(): string {
        return this.recinto ? (this.editMode ? 'Editar' : 'Detalle del') : 'Nuevo'; // work mode: edition or creation
    }

    get titlePI(): string {
        if (!this.recinto) return '';

        switch (this.recinto?.IdTipoRecinto) {
            case EnumIdsTcTipoSolicitudCam.ID_PARCELA:
                return EnumDescripcionTcTipoSolicitudCamSingular.PARCELA;
            case EnumIdsTcTipoSolicitudCam.ID_INVERNADERO:
                return EnumDescripcionTcTipoSolicitudCamSingular.INVERNADERO;
            default:
                return EnumDescripcionTcTipoSolicitudCamSingular.RECINTO;
        }
    }
}
